import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Icon } from './Icon';
import { Modal } from './Modal';

/**
 * THE REASON TABLE — a verb that cannot fire until somebody says why.
 *
 * Six boards across four lanes draw the identical thing: a two-column table of
 * reasons and what each one DOES, ending in "Other — write the reason", with an
 * optional note that becomes required the moment Other is picked. It is the
 * desk's move/leave/cancel/no-show, the manager's revoke and deny-&-return, and
 * the doctor's deny-a-reopen.
 *
 * WHY NOT THE KIT'S OTHER ReasonPicker. That one appends its own "Other" row
 * with its own consequence line ("Nothing is assumed. Your note is the
 * record."); every one of these boards prints a different one, word for word —
 * "closes with your line attached — the line is required". Static copy is
 * character-for-character, so the two coexist rather than one bending.
 *
 * Two rules the boards share and this enforces:
 *  1. A reason must be picked before the verb fires.
 *  2. The note is optional, and REQUIRED when the reason is "Other".
 *
 * THE CONFIRM BUTTON IS NEVER DEAD. It stays full colour whatever is picked and
 * says what is still missing when pressed, rather than greying out and leaving
 * the person to guess which of the two rules they have not met.
 */

export interface ReasonRow {
  key: string;
  label: string;
  /** What picking it does. The boards' second column, never decoration. */
  consequence: string;
}

/** Every one of the four boards ends its table with this row, word for word. */
export const OTHER_REASON: ReasonRow = {
  key: 'other',
  label: 'Other — write the reason',
  consequence: 'closes with your line attached — the line is required',
};

export function ReasonTable({
  open,
  onClose,
  title,
  sub,
  reasons,
  notePlaceholder,
  confirmLabel,
  before,
  after,
  busy,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  sub: ReactNode;
  /** The board's rows, in the board's order. "Other" is appended here. */
  reasons: ReasonRow[];
  /** The board's own example line, shown as the field's placeholder. */
  notePlaceholder: string;
  confirmLabel: string;
  /** The capture a reason alone cannot carry — the move's doctor picker. */
  before?: ReactNode;
  /** What the board draws UNDER the note — the void's "nothing is silent". */
  after?: ReactNode;
  busy?: boolean;
  onConfirm: (reason: ReasonRow, note: string) => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState('');

  /* A picker that is closed and re-opened starts blank — the last reason is
     not a suggestion for the next patient. */
  useEffect(() => {
    if (!open) {
      setPicked(null);
      setNote('');
    }
  }, [open]);

  if (!open) return null;

  const rows = [...reasons, OTHER_REASON];
  const chosen = rows.find((r) => r.key === picked) ?? null;
  const noteRequired = chosen?.key === OTHER_REASON.key;
  const ready = chosen !== null && (!noteRequired || note.trim().length > 0);

  return (
    <Modal
        open={open}
        onClose={onClose}
        title={title}
        sub={sub}
        width={620}
        foot={
          <>
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            {/* Full colour whatever is picked, as every board draws it — and
                never dead: it says what is still missing instead. */}
            <button
              type="button"
              className="btn kit-btn-warn"
              aria-disabled={!ready}
              disabled={busy}
              onClick={() => {
                if (!chosen) {
                  toast.info('Pick a reason — it is what the row is closed with.');
                  return;
                }
                if (noteRequired && !note.trim()) {
                  toast.info('Write the reason — the line is required when you pick “Other”.');
                  return;
                }
                onConfirm(chosen, note.trim());
              }}
            >
              {confirmLabel}
            </button>
          </>
        }
      >
        {before}

        <table className="dtable" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ width: '48%' }}>Reason</th>
              <th style={{ width: '52%' }}>What it does</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className={picked === r.key ? 'rowc sel' : 'rowc'}
                aria-selected={picked === r.key}
                onClick={() => setPicked(r.key)}
              >
                <td>
                  <span className="t-body">{r.label}</span>
                </td>
                <td>
                  <span className="t-sec sec">{r.consequence}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="card card-ghost mt12">
          <label className="t-ovl mut" htmlFor="kit-reason-note" style={{ display: 'block' }}>
            Note{' '}
            <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
              {noteRequired
                ? '(required — you picked “Other”)'
                : '(optional — required if you pick “Other”)'}
            </span>
          </label>
          <p className="t-sec sec mt4 kit-notefield">
            <input
              id="kit-reason-note"
              className="t-sec sec"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={notePlaceholder}
            />
            <Icon name="pencil" className="ic-sm" />
          </p>
        </div>

      {after}
    </Modal>
  );
}
